/**
 * Filter Utilities - Shared filter logic for components
 * 
 * This file provides reusable filter state management and utilities
 * that can be used across different components like Roster, ForecastVsActuals, etc.
 */

// Filter state reducer for better performance
export const filterReducer = (state, action) => {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, searchName: action.payload };
    case 'SET_DEBOUNCED_SEARCH':
      return { ...state, debouncedSearchName: action.payload };
    case 'SET_COMPANY_FILTER':
      return { ...state, companyFilter: action.payload };
    case 'SET_LOCATION_FILTER':
      return { ...state, locationFilter: action.payload };
    case 'SET_FUNCTION_FILTER':
      return { ...state, functionFilter: action.payload };
    case 'SET_OUTCOME_FILTER':
      return { ...state, outcomeFilter: action.payload };
    case 'SET_APPLICATION_FILTER':
      return { ...state, applicationFilter: action.payload };
    case 'SET_TAG_FILTER':
      return { ...state, tagFilter: action.payload };
    case 'SET_ALLOCATION_TYPE_FILTER':
      return { ...state, allocationTypeFilter: action.payload };
    case 'SET_ALLOCATION_STATUS_FILTER':
      return { ...state, allocationStatusFilter: action.payload };
    case 'SET_DEPARTMENT_FILTER':
      return { ...state, departmentFilter: action.payload };
    case 'CLEAR_ALL_FILTERS':
      return {
        ...state,
        searchName: '',
        debouncedSearchName: '',
        companyFilter: [],
        locationFilter: [],
        functionFilter: [],
        outcomeFilter: [],
        applicationFilter: [],
        tagFilter: [],
        departmentFilter: [],
        allocationTypeFilter: ['staffed'], // Reset to default
        allocationStatusFilter: []
      };
    case 'SET_MULTIPLE_FILTERS':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

// Initial filter state
export const initialFilterState = {
  searchName: '',
  debouncedSearchName: '',
  companyFilter: [],
  locationFilter: [],
  functionFilter: [],
  outcomeFilter: [],
  applicationFilter: [],
  tagFilter: [],
  allocationTypeFilter: ['staffed'], // Default to staffed only
  allocationStatusFilter: [],
  departmentFilter: []
};

/**
 * Calculate the number of active filters
 */
export const getActiveFiltersCount = (filterState) => {
  let count = 0;
  
  if (filterState.debouncedSearchName) count++;
  if (filterState.companyFilter && filterState.companyFilter.length > 0) count++;
  if (filterState.locationFilter && filterState.locationFilter.length > 0) count++;
  if (filterState.functionFilter && filterState.functionFilter.length > 0) count++;
  if (filterState.outcomeFilter && filterState.outcomeFilter.length > 0 && !filterState.outcomeFilter.includes('all')) count++;
  if (filterState.applicationFilter && filterState.applicationFilter.length > 0 && !filterState.applicationFilter.includes('all')) count++;
  if (filterState.tagFilter && filterState.tagFilter.length > 0 && !filterState.tagFilter.includes('all')) count++;
  if (filterState.allocationTypeFilter && filterState.allocationTypeFilter.length > 0 && 
      JSON.stringify(filterState.allocationTypeFilter) !== JSON.stringify(['staffed'])) count++;
  if (filterState.allocationStatusFilter && filterState.allocationStatusFilter.length > 0) count++;
  if (filterState.departmentFilter && filterState.departmentFilter.length > 0) count++;

  return count;
};

/**
 * Safe version of getActiveFiltersCount with null checks
 */
export const safeGetActiveFiltersCount = (filterState) => {
  if (!filterState) return 0;
  
  try {
    return getActiveFiltersCount(filterState);
  } catch (error) {
    console.warn('Error calculating active filters count:', error);
    return 0;
  }
};

/**
 * Check if a member matches the search filter
 */
export const matchesSearchFilter = (member, searchTerm) => {
  if (!searchTerm) return true;
  
  const searchLower = searchTerm.toLowerCase();
  const memberName = (member.name || '').toLowerCase();
  const memberRole = (member.role || '').toLowerCase();
  const memberFunction = (member.function || '').toLowerCase();
  
  return memberName.includes(searchLower) || 
         memberRole.includes(searchLower) || 
         memberFunction.includes(searchLower);
};

/**
 * Check if a member matches the company filter
 */
export const matchesCompanyFilter = (member, companyFilter) => {
  if (!companyFilter || companyFilter.length === 0) return true;
  return companyFilter.includes(member.company);
};

/**
 * Check if a member matches the location filter
 */
export const matchesLocationFilter = (member, locationFilter) => {
  if (!locationFilter || locationFilter.length === 0) return true;
  return locationFilter.includes(member.location);
};

/**
 * Check if a member matches the function filter
 */
export const matchesFunctionFilter = (member, functionFilter) => {
  if (!functionFilter || functionFilter.length === 0) return true;
  return functionFilter.includes(member.function);
};

/**
 * Check if a member matches the outcome filter
 */
export const matchesOutcomeFilter = (member, outcomeFilter, memberOutcomes = []) => {
  if (!outcomeFilter || outcomeFilter.length === 0 || outcomeFilter.includes('all')) return true;
  
  // For unassigned members, check their outcome
  if (member.isUnassigned) {
    const outcomeName = member.unassignedRoleData?.outcome_name || member.outcome_name;
    return outcomeFilter.includes(outcomeName);
  }
  
  // For regular members, check if any of their outcomes match
  return memberOutcomes.some(outcome => outcomeFilter.includes(outcome));
};

/**
 * Check if a member matches the tag filter
 */
export const matchesTagFilter = (member, tagFilter, memberTags = new Set()) => {
  if (!tagFilter || tagFilter.length === 0 || tagFilter.includes('all')) return true;
  
  // Check if any of the member's tags match the filter
  return tagFilter.some(filterTag => memberTags.has(filterTag));
};

/**
 * Check if a member matches the allocation type filter
 */
export const matchesAllocationTypeFilter = (member, allocationTypeFilter) => {
  if (!allocationTypeFilter || allocationTypeFilter.length === 0) return true;
  
  if (member.isUnassigned) {
    return allocationTypeFilter.includes('unassigned');
  } else {
    return allocationTypeFilter.includes('staffed');
  }
};

/**
 * Check if a member matches the allocation status filter
 */
export const matchesAllocationStatusFilter = (member, allocationStatusFilter) => {
  if (!allocationStatusFilter || allocationStatusFilter.length === 0) return true;
  
  const memberStatus = member.allocationStatus || 'Not Allocated';
  return allocationStatusFilter.includes(memberStatus);
};

/**
 * Apply all filters to a member
 */
export const applyAllFilters = (member, filterState, memberOutcomes = [], memberTags = new Set()) => {
  return matchesSearchFilter(member, filterState.debouncedSearchName) &&
         matchesCompanyFilter(member, filterState.companyFilter) &&
         matchesLocationFilter(member, filterState.locationFilter) &&
         matchesFunctionFilter(member, filterState.functionFilter) &&
         matchesOutcomeFilter(member, filterState.outcomeFilter, memberOutcomes) &&
         matchesTagFilter(member, filterState.tagFilter, memberTags) &&
         matchesAllocationTypeFilter(member, filterState.allocationTypeFilter) &&
         matchesAllocationStatusFilter(member, filterState.allocationStatusFilter);
};

/**
 * Get filter summary for debugging
 */
export const getFilterSummary = (filterState) => {
  return {
    activeFilters: getActiveFiltersCount(filterState),
    filters: {
      search: filterState.debouncedSearchName,
      company: filterState.companyFilter,
      location: filterState.locationFilter,
      function: filterState.functionFilter,
      outcome: filterState.outcomeFilter,
      application: filterState.applicationFilter,
      tag: filterState.tagFilter,
      allocationType: filterState.allocationTypeFilter,
      allocationStatus: filterState.allocationStatusFilter
    }
  };
};